import {
  toolCallResultSchema,
  ToolCallResultStatus,
} from 'common/src/modules/creativeTemplates/copilot/copilotCreativeTemplates.consts';
import appendCustomCallerToCallChain from 'common/src/modules/shared/copilot/utils/creators/appendCustomCallerToCallChain';
import openAISystemMessage from 'common/src/modules/shared/copilot/openAI/utils/openAISystemMessage';
import openAIUserMessage from 'common/src/modules/shared/copilot/openAI/utils/openAIUserMessage';
import {Caller, ToolUtils} from 'common/src/modules/shared/copilot/copilot.types';
import {z} from 'zod';
import {transformAnimationSchema} from '../adBuilderAgent/adBuilderAgent.schemas';

// Type for the parsed transform parameters
type TransformParams = z.infer<typeof transformAnimationSchema>;

export interface TransformParseResult {
  transformParams: TransformParams;
  unsupportedRequest?: string;
}

export interface CurrentLayerTransform {
  position: {x: number; y: number; z: number};
  anchor: {x: number; y: number; z: number};
  scale: {x: number; y: number; z: number};
  rotationZ: number;
  rotationX?: number;
  rotationY?: number;
  opacity: number;
  boundingBox: {x: number; y: number; w: number; h: number};
}

export async function parseTransformFromNaturalLanguage(
  transformExplained: string,
  compWidth: number,
  compHeight: number,
  toolUtils: ToolUtils,
  callChain: Caller[],
  currentLayerTransform?: CurrentLayerTransform,
): Promise<TransformParseResult> {
  const systemPrompt = [
    'You are requested to receive a natural language description of a layer transform (inside an ad composition) and convert it into structured animation transform parameters and call the tool.',
    'IMPORTANT: Only call the tool once with all transform changes. Do not provide any text response.',
  ].join(' ');

  const currentTransformSection = currentLayerTransform
    ? (() => {
        const bb = currentLayerTransform.boundingBox;
        const an = currentLayerTransform.anchor;
        const sc = currentLayerTransform.scale;
        const compW = compWidth;
        const compH = compHeight;
        const centerX = bb.x + bb.w / 2;
        const centerY = bb.y + bb.h / 2;
        const pCenterX = (compW / 2 - (sc.x / 100) * (centerX - an.x)).toFixed(2);
        const pCenterY = (compH / 2 - (sc.y / 100) * (centerY - an.y)).toFixed(2);
        const pTopLeftX = (-(sc.x / 100) * (bb.x - an.x)).toFixed(2);
        const pTopLeftY = (-(sc.y / 100) * (bb.y - an.y)).toFixed(2);

        return `
Current layer transform (use these values as baseline for relative/delta requests like "move right", "scale up", "rotate 90 degrees"):
- Position: x=${currentLayerTransform.position.x}, y=${currentLayerTransform.position.y}, z=${currentLayerTransform.position.z}
- Anchor: x=${an.x}, y=${an.y}, z=${an.z}
- Scale: x=${sc.x}, y=${sc.y}, z=${sc.z} (100 = normal)
- Rotation Z: ${currentLayerTransform.rotationZ} degrees${currentLayerTransform.rotationX !== undefined ? `, X: ${currentLayerTransform.rotationX} degrees` : ''}${currentLayerTransform.rotationY !== undefined ? `, Y: ${currentLayerTransform.rotationY} degrees` : ''}
- Opacity: ${currentLayerTransform.opacity} (0-100)

PLACEMENT: p = anchor position in comp (NOT top-left). Formula: p = desiredWorld - (scale/100)*(bbRef - anchor). Comp: ${compW}x${compH}. bb: ${bb.x},${bb.y} ${bb.w}x${bb.h}.
- center → p = (${pCenterX}, ${pCenterY})
- top-left → p = (${pTopLeftX}, ${pTopLeftY})
- top-right → p = (${(compW - (sc.x / 100) * (bb.x + bb.w - an.x)).toFixed(2)}, ${pTopLeftY})
- bottom-left → p = (${pTopLeftX}, ${(compH - (sc.y / 100) * (bb.y + bb.h - an.y)).toFixed(2)})
- bottom-right → p = (${(compW - (sc.x / 100) * (bb.x + bb.w - an.x)).toFixed(2)}, ${(compH - (sc.y / 100) * (bb.y + bb.h - an.y)).toFixed(2)})
Use these pre-computed values when user asks for placement.`;
      })()
    : '';

  const userPrompt = `Composition size: ${compWidth}x${compHeight} pixels${currentTransformSection}

Transform request: "${transformExplained}"

Convert this natural language description into structured transform parameters and call the tool.
Consider:
- Do Not send to tool any transform property which does not require changing.
- For relative requests (e.g. "move 50px right", "scale up 20%"), use the current layer transform above to compute the new absolute values.
- For static changes, use time: 0. For animations, create multiple keyframes with appropriate timing.
- Position/Scale/Anchor: z is optional for 3D transforms. for 2D rotation, use rotationZ
- Rotation X/Y/Z and Opacity: they are all 1D values, so use must use the x value in keyframes only`;
  const response = await toolUtils.invokeStandaloneLLMCall({
    messages: [openAISystemMessage(systemPrompt), openAIUserMessage(userPrompt)],
    responseSchema: toolCallResultSchema.extend({
      result: transformAnimationSchema,
    }),
    buildResponseUsingToolCall: true,
    callChain: appendCustomCallerToCallChain('parseTransformFromNaturalLanguage', callChain),
  });

  // Check if LLM indicated failure
  if (response.status === ToolCallResultStatus.Failure) {
    const reason = response.failureReason || 'Request failed without specific reason';
    throw new Error(`Transform edit failed: ${reason}`);
  }

  return {
    transformParams: response.result,
    ...(response.unsupportedRequestPart && {unsupportedRequest: response.unsupportedRequestPart}),
  };
}
